import React, { useState, useEffect } from 'react';
import { Database, Download, Calendar, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { exportToExcel } from '../utils/exportData';

const DataManagement = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        totalInvoices: 0,
        totalBookings: 0,
        totalEarnings: 0
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    
    // Date Range State
    const today = new Date().toISOString().split('T')[0];
    const fifteenDaysAgo = new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const [dateRange, setDateRange] = useState({
        from: fifteenDaysAgo,
        to: today
    });
    const [error, setError] = useState('');

    useEffect(() => {
        const loadData = async () => {
            if (!user) return;

            try {
                setIsLoading(true);

                const { count: totalBookingsCount, error: totalBookingsError } = await supabase
                    .from('bookings')
                    .select('id', { count: 'exact', head: true })
                    .eq('user_id', user.id);
                if (totalBookingsError) throw totalBookingsError;

                const { data: allInvoices, error: statsError } = await supabase
                    .from('invoices')
                    .select('grand_total')
                    .eq('user_id', user.id);

                if (statsError) throw statsError;

                const totalInvoices = allInvoices.length;
                const totalEarnings = allInvoices.reduce((sum, invoice) => sum + (invoice.grand_total || 0), 0);

                setStats({
                    totalInvoices,
                    totalBookings: totalBookingsCount || 0,
                    totalEarnings
                });
            } catch (error) {
                console.error('Error loading stats:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [user]);

    const validateRange = () => {
        const start = new Date(dateRange.from);
        const end = new Date(dateRange.to);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (start > end) {
            setError('Start date cannot be after end date.');
            return false;
        }
        
        if (diffDays > 15) {
            setError('Maximum export range is 15 days. Please select a smaller range.');
            return false;
        }
        
        setError('');
        return true;
    };

    const handleExportData = async (dataType = 'all') => {
        if (!user) return;
        if (!validateRange()) return;

        try {
            setIsExporting(true);
            await exportToExcel(user.id, dataType, { 
                fromDate: dateRange.from, 
                toDate: dateRange.to 
            });
        } catch (error) {
            console.error('Error exporting data:', error);
            alert('Failed to export data. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-6 max-w-6xl mx-auto pb-12">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                        <Database className="h-8 w-8 text-orange-500" />
                        Data Management
                    </h1>
                    <p className="text-slate-500 font-medium mt-1">Precise franchise operation control and bulk records export</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-bold border border-emerald-100">
                    <CheckCircle2 className="h-4 w-4" />
                    System Status: Secure & Operational
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Export Control Panel */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="bg-yellow-50 px-8 py-4 border-b border-slate-200 flex items-center justify-between">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <Calendar className="h-5 w-5 text-indigo-600" />
                                Export Configuration
                            </h3>
                            <span className="text-xs font-black uppercase tracking-widest text-slate-400 bg-white px-3 py-1 rounded-full border border-slate-200">Step 01: Select Range</span>
                        </div>
                        
                        <div className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">From Date</label>
                                    <input 
                                        type="date"
                                        value={dateRange.from}
                                        onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
                                        className="w-full px-4 py-3 bg-yellow-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all font-medium text-slate-900"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">To Date</label>
                                    <input 
                                        type="date"
                                        value={dateRange.to}
                                        onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
                                        className="w-full px-4 py-3 bg-yellow-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all font-medium text-slate-900"
                                    />
                                </div>
                            </div>

                            {error ? (
                                <div className="mb-8 p-4 bg-orange-50 border border-orange-100 rounded-xl flex items-start gap-3 text-orange-600 animate-in fade-in slide-in-from-top-2">
                                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                                    <div className="text-sm font-bold">{error}</div>
                                </div>
                            ) : (
                                <div className="mb-8 p-4 bg-indigo-50 border border-indigo-100 rounded-xl flex items-start gap-3 text-indigo-600">
                                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                                    <div className="text-sm font-medium">Valid Range: You can export up to 15 days of operational logs at a time to ensure system stability.</div>
                                </div>
                            )}

                            <div className="space-y-4">
                                <div className="flex items-center gap-2 mb-4">
                                   <span className="text-xs font-black uppercase tracking-widest text-slate-400">Step 02: Choose format</span>
                                   <div className="h-px bg-slate-100 flex-1"></div>
                                </div>
                                
                                <button
                                    onClick={() => handleExportData('all')}
                                    disabled={isExporting || !!error}
                                    className="w-full flex items-center justify-between p-6 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-100 disabled:text-slate-400 text-white rounded-2xl transition-all shadow-lg shadow-orange-500/20 group"
                                >
                                    <div className="text-left">
                                        <div className="font-black text-xl tracking-tight uppercase">Export Master Report</div>
                                        <div className="text-sm font-medium opacity-80 group-disabled:opacity-100">Consolidated Invoices & Consignments</div>
                                    </div>
                                    <div className="h-12 w-12 bg-white/20 rounded-xl flex items-center justify-center">
                                       <Download className={`h-6 w-6 ${isExporting ? 'animate-bounce' : ''}`} />
                                    </div>
                                </button>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <button
                                        onClick={() => handleExportData('invoices')}
                                        disabled={isExporting || !!error}
                                        className="flex items-center justify-between p-5 bg-white border-2 border-slate-100 hover:border-indigo-500 hover:bg-indigo-50 rounded-2xl transition-all group"
                                    >
                                        <span className="font-bold text-slate-700 group-hover:text-indigo-900 uppercase tracking-tight">Invoice Logs</span>
                                        <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
                                    </button>
                                    <button
                                        onClick={() => handleExportData('bookings')}
                                        disabled={isExporting || !!error}
                                        className="flex items-center justify-between p-5 bg-white border-2 border-slate-100 hover:border-indigo-500 hover:bg-indigo-50 rounded-2xl transition-all group"
                                    >
                                        <span className="font-bold text-slate-700 group-hover:text-indigo-900 uppercase tracking-tight">Consignment Lists</span>
                                        <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Info Panel */}
                <div className="space-y-6">
                    <div className="bg-indigo-950 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 -tr-1/4 opacity-10">
                            <Database className="h-40 w-40" />
                        </div>
                        <h4 className="text-sm font-black uppercase tracking-widest text-indigo-400 mb-6">Cloud Ledger Summary</h4>
                        
                        {isLoading ? (
                            <div className="animate-pulse space-y-6">
                                <div className="h-10 bg-white/10 rounded-lg"></div>
                                <div className="h-10 bg-white/10 rounded-lg"></div>
                                <div className="h-10 bg-white/10 rounded-lg"></div>
                            </div>
                        ) : (
                            <div className="space-y-6 relative z-10">
                                <div>
                                    <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-1">Total Invoices</div>
                                    <div className="text-3xl font-black">{stats.totalInvoices.toLocaleString()}</div>
                                </div>
                                <div>
                                    <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-1">Total Consignments</div>
                                    <div className="text-3xl font-black">{stats.totalBookings.toLocaleString()}</div>
                                </div>
                                <div className="pt-6 border-t border-white/10">
                                    <div className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-1">Total Revenue Managed</div>
                                    <div className="text-3xl font-black text-orange-500">₹{stats.totalEarnings.toLocaleString()}</div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                        <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">Export Protocol</h4>
                        <ul className="space-y-4">
                            <li className="flex gap-3 text-sm font-medium text-slate-600 leading-relaxed">
                                <div className="h-5 w-5 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">1</div>
                                Select a date range not exceeding 15 calendar days.
                            </li>
                            <li className="flex gap-3 text-sm font-medium text-slate-600 leading-relaxed">
                                <div className="h-5 w-5 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">2</div>
                                Choose the report depth (Master, Invoices, or Consignments).
                            </li>
                            <li className="flex gap-3 text-sm font-medium text-slate-600 leading-relaxed">
                                <div className="h-5 w-5 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">3</div>
                                An .xlsx file will be generated and downloaded instantly.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataManagement;
